import type { ReactNode } from 'react';
import {
    dataTableButtonClasses,
    type DataTableButtonSize,
    type DataTableButtonVariant
} from '../buttonStyles';
import type { DataTableColumn } from '../types';
import { buildColumn, type ColumnPresetOptions } from './base';

export type ActionsColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The row's menu/controls. Anything - a kebab menu, an icon row, a badge. */
    render: (row: T, index: number) => ReactNode;
    /** Classes on the flex wrapper. */
    contentClassName?: string;
};

/**
 * Trailing column for a row menu or bespoke controls.
 *
 * Defaults that make it correct without thinking about it: `align: 'right'`,
 * `stopClickPropagation: true` (so opening the menu never also activates the
 * row), `truncate: false` (a clipped cell would cut off an open popover) and a
 * blank header.
 */
export function actionsColumn<T, K extends string = string>(
    options: ActionsColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { render, contentClassName } = options;
    return buildColumn(
        options,
        (row, index) => (
            <div className={`flex items-center justify-end gap-1 ${contentClassName ?? ''}`}>
                {render(row, index)}
            </div>
        ),
        { align: 'right', truncate: false, stopClickPropagation: true }
    );
}

/** One inline button in a `buttonColumn` cell. */
export interface RowButton<T> {
    /** Stable React key. Defaults to the button's index in the array. */
    key?: string;
    /** Button text. A function lets the label depend on the row. */
    label: ReactNode | ((row: T) => ReactNode);
    onClick: (row: T, index: number) => void;
    variant?: DataTableButtonVariant;
    /** Rendered before the label. */
    icon?: ReactNode;
    disabled?: (row: T) => boolean;
    /** Drop the button entirely for this row. */
    hidden?: (row: T) => boolean;
    getTitle?: (row: T) => string | undefined;
    /** Accessible name, for icon-only buttons. */
    getAriaLabel?: (row: T) => string | undefined;
    className?: string;
}

export type ButtonColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    buttons: ReadonlyArray<RowButton<T>>;
    /** Defaults to `'sm'`, the row-height-friendly size. */
    size?: DataTableButtonSize;
    contentClassName?: string;
};

/**
 * One or more inline buttons per row.
 *
 * `stopClickPropagation` defaults to **on**: a button inside a clickable row
 * must not also fire the row's action, by click or by Enter.
 */
export function buttonColumn<T, K extends string = string>(
    options: ButtonColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { buttons, size = 'sm', contentClassName } = options;
    const align = options.align ?? 'left';

    return buildColumn(
        options,
        (row, index) => (
            <div
                className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''} ${contentClassName ?? ''}`}
            >
                {buttons.map((button, buttonIndex) => {
                    if (button.hidden?.(row)) {
                        return null;
                    }
                    return (
                        <button
                            key={button.key ?? buttonIndex}
                            type="button"
                            onClick={() => button.onClick(row, index)}
                            disabled={button.disabled?.(row)}
                            title={button.getTitle?.(row)}
                            aria-label={button.getAriaLabel?.(row)}
                            className={dataTableButtonClasses(
                                button.variant,
                                size,
                                button.className
                            )}
                        >
                            {button.icon}
                            {typeof button.label === 'function' ? button.label(row) : button.label}
                        </button>
                    );
                })}
            </div>
        ),
        { align, truncate: false, stopClickPropagation: true }
    );
}
